import { Typography } from '@mui/material';
import { Edges, OrbitControls, useGLTF } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';
import { Fragment, useState } from 'react';
import { fabricationFiltersAdmin } from 'src/components/3d/Assemblies';
import { ButtonRow } from 'src/components/common';
import { CategoryType } from 'src/constants/category';
import { useEnv } from 'src/context/EnvProvider';
import useFilters from 'src/hook/useFilter';
import { Buckets } from 'src/services/file';
import { createPiece } from 'src/services/pieces';
import { upload3dPiece, uploadImage } from 'src/utils/file.utils';
import {
  AxisInputGroup,
  BlockedAxisSelector,
  CategorySelect,
  MaterialSelect,
  PieceDetailsForm,
} from './components';
import { ButtonContainer, Input, StyledButton } from './styles';
import { AxisInput, PieceDetails } from './types';
import { castPieceDetails } from './utils';

type PieceImageProps = {
  pieceUrl: string;
  name: string | undefined;
  fileName: string | undefined;
  reset: () => void;
  isTimeToSave: boolean;
  setIsTimeToSave: (isTimeToSave: boolean) => void;
  category: CategoryType;
  material: 'GRANIT' | 'INOX';
  fabricationFilterState: string[];
  blockedAxis: string[];
  polishing: { x: string; y: string; z: string };
  sawing: { x: string; y: string; z: string };
  extra: {
    french: string;
    import: string;
  };
  details: {
    french: { linear: PieceDetails; m2: PieceDetails };
    import: { linear: PieceDetails; m2: PieceDetails };
  };
};

const PieceImage = ({
  pieceUrl,
  name,
  fileName,
  reset,
  isTimeToSave,
  setIsTimeToSave,
  category,
  material,
  fabricationFilterState,
  blockedAxis,
  polishing,
  sawing,
  details,
  extra,
}: PieceImageProps) => {
  const gltf = useGLTF(pieceUrl);

  const pieces =
    gltf.scene.children[0].children.length > 0
      ? gltf.scene.children[0].children
      : gltf.scene.children;

  const gl = useThree((state) => state.gl);

  const savePiece = () => {
    gl.domElement.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'image.png');

        const pictureName = await uploadImage(file, Buckets.BUCKET_3D_PIECES);
        if (name && fileName && pictureName) {
          await createPiece(
            name,
            fileName,
            pictureName,
            category,
            material,
            fabricationFilterState,
            blockedAxis,
            {
              x: Number.parseFloat(polishing.x),
              y: Number.parseFloat(polishing.y),
              z: Number.parseFloat(polishing.z),
            },
            {
              x: Number.parseFloat(sawing.x),
              y: Number.parseFloat(sawing.y),
              z: Number.parseFloat(sawing.z),
            },
            {
              french: {
                extra: Number.parseFloat(extra.french),
                linear: castPieceDetails(details.french.linear),
                m2: castPieceDetails(details.french.m2),
              },
              import: {
                extra: Number.parseFloat(extra.import),
                linear: castPieceDetails(details.import.linear),
                m2: castPieceDetails(details.import.m2),
              },
            },
          );
          reset();
        }
      }
    });
  };

  if (isTimeToSave) {
    savePiece();
    setIsTimeToSave(false);
  }

  return (
    <>
      <OrbitControls />

      <ambientLight />
      <pointLight position={[5, 2, 5]} intensity={0.5} />
      <pointLight position={[-5, 2, 5]} intensity={0.5} />
      <pointLight position={[0, 2, 5]} intensity={0.5} />
      <pointLight position={[0, 2, -5]} intensity={0.5} />

      {pieces.map((piece: any, index: number) => {
        return (
          <Fragment key={index}>
            <mesh key={index} geometry={piece.geometry} position={piece.position}>
              <>
                <Edges />
                <meshStandardMaterial />
              </>
            </mesh>
          </Fragment>
        );
      })}
    </>
  );
};

export const ImportComponent = () => {
  const { buckets } = useEnv();

  const [category, setCategory] = useState<CategoryType>('ANY');
  const [material, setMaterial] = useState<'GRANIT' | 'INOX'>('GRANIT');
  const [fileName, setFileName] = useState<string>();
  const [blockedAxis, setBlockedAxis] = useState<string[]>([]);
  const [name, setName] = useState<string>();
  const [isTimeToSave, setIsTimeToSave] = useState<boolean>(false);
  const [fabricationFiltersComponent, fabricationFilterState] = useFilters(
    fabricationFiltersAdmin,
    [],
  );
  const [details, setDetails] = useState<{
    french: { linear: PieceDetails; m2: PieceDetails };
    import: { linear: PieceDetails; m2: PieceDetails };
  }>({
    french: {
      linear: {
        faceX: '0',
        faceX2: '0',
        faceY: '0',
        faceY2: '0',
        faceZ: '0',
        faceZ2: '0',
      },
      m2: {
        faceX: '0',
        faceX2: '0',
        faceY: '0',
        faceY2: '0',
        faceZ: '0',
        faceZ2: '0',
      },
    },
    import: {
      linear: {
        faceX: '0',
        faceX2: '0',
        faceY: '0',
        faceY2: '0',
        faceZ: '0',
        faceZ2: '0',
      },
      m2: {
        faceX: '0',
        faceX2: '0',
        faceY: '0',
        faceY2: '0',
        faceZ: '0',
        faceZ2: '0',
      },
    },
  });
  const [polishing, setPolishing] = useState<AxisInput>({ x: '1', y: '1', z: '1' });
  const [sawing, setSawing] = useState<AxisInput>({ x: '2', y: '2', z: '2' });
  const [extra, setExtra] = useState<{ french: string; import: string }>({
    french: '0',
    import: '0',
  });

  const reset = () => {
    setFileName(undefined);
    setName(undefined);
  };

  const changeDetails = (
    country: 'french' | 'import',
    mode: 'linear' | 'm2',
    key: keyof PieceDetails,
    value: string,
  ) => {
    setDetails({
      ...details,
      [country]: {
        ...details[country],
        [mode]: { ...details[country][mode], [key]: value },
      },
    });
  };

  const handleExtraChange = (country: 'french' | 'import', value: string) => {
    setExtra({ ...extra, [country]: value });
  };

  return (
    <>
      <Input
        value={name}
        type="text"
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <CategorySelect category={category} setCategory={setCategory} />
      <MaterialSelect material={material} setMaterial={setMaterial} style={{marginBottom: '8px' }} />
      <>
        <BlockedAxisSelector blockedAxis={blockedAxis} setBlockedAxis={setBlockedAxis} />
        <AxisInputGroup label="Polissage" value={polishing} onChange={setPolishing} />
        <AxisInputGroup label="Sciage" value={sawing} onChange={setSawing} />
        <PieceDetailsForm
          details={details}
          extra={extra}
          onChangeDetails={changeDetails}
          onChangeExtra={handleExtraChange}
        />
      </>

      <ButtonRow>{fabricationFiltersComponent}</ButtonRow>

      <Input
        type="file"
        onChange={async (e: any) => {
          if (e.target.files && e.target.files[0]) {
            const pieceFileName = await upload3dPiece(e.target.files[0]);
            setFileName(pieceFileName ?? undefined);
          }
        }}
      />

      {fileName ? (
        <>
          <Canvas
            style={{
              width: '300px',
              height: '300px',
              border: '1px solid black',
            }}
            camera={{
              position: [1.5, 1, 4],
              fov: 40,
            }}
            dpr={[1, 2]}
            gl={{ preserveDrawingBuffer: true }}
            raycaster={{
              params: {
                Points: { threshold: 0.01 },
                Line: { threshold: 0.01 },
              },
            }}
          >
            <PieceImage
              pieceUrl={`${buckets?.BUCKET_3D_PIECES ?? ''}/${fileName}`}
              name={name}
              fileName={fileName}
              reset={() => reset()}
              setIsTimeToSave={setIsTimeToSave}
              isTimeToSave={isTimeToSave}
              category={category}
              material={material}
              fabricationFilterState={fabricationFilterState}
              blockedAxis={blockedAxis}
              polishing={polishing}
              sawing={sawing}
              details={details}
              extra={extra}
            />
          </Canvas>

          <ButtonContainer>
            <StyledButton variant="contained" onClick={() => setIsTimeToSave(true)}>
              <Typography variant="body1" style={{ fontSize: '12px', textTransform: 'capitalize' }}>
                Save piece
              </Typography>
            </StyledButton>
          </ButtonContainer>
        </>
      ) : null}
    </>
  );
};
